import React, { useCallback, useContext } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { useSelector } from 'react-redux'
import { SkillBiteEditSlice } from '../../../../../reducers'
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { SectionIcon } from '../icons'
import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useConfirm } from 'material-ui-confirm'
import { multiNoWrap } from '../../../../../util/multi-no-wrap'
import { BuildNavigationContext } from '../..'
const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    paddingBottom: '10px',
    borderBottom: '1px solid #E0E0E0',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between'
  },
  deleteBtn: {
    color: palette.error.main,
    marginLeft: spacing(1),
    minWidth: 135
  },
  sectionName: {
    ...multiNoWrap(1)
  }
}))

const PageSelector = () => {
  const classes = useStyles()
  const selectedSkillBite = useSelector(SkillBiteEditSlice.selectors.selectSkillBite)
  const selectedSession = useSelector(SkillBiteEditSlice.selectors.selectSelectedSession)
  const selectedSection = useSelector(SkillBiteEditSlice.selectors.selectSelectedSection)
  const selectedPage = useSelector(SkillBiteEditSlice.selectors.selectSelectedPage)
  const canEditStructure = useSelector(SkillBiteEditSlice.selectors.selectCanEditStructure)
  const { changeSelectedPage, discardChangesOnNavigationGuard } = useContext(BuildNavigationContext)
  const confirm = useConfirm()

  const [addPage, deletePage] = useEvent([
    SkillBiteEditSlice.eventBus.addPage,
    SkillBiteEditSlice.eventBus.deletePage
  ])

  const handleChangePage = useCallback((page) => {
    changeSelectedPage(page)
  }, [changeSelectedPage])

  const handleAddPage = () => {
    discardChangesOnNavigationGuard(() => {
      addPage.trigger({
        skillBiteId: selectedSkillBite.id,
        sessionId: selectedSession.id,
        sectionId: selectedSection.id,
        order: selectedSection.pages.length + 1
      })
    })
  }

  const handleDeletePage = () => {
    confirm({
      title: 'Delete page',
      description: 'This is a destructive action. Are you sure you want to delete this page?',
      confirmationText: 'Yes, delete it.',
      confirmationButtonProps: {
        id: 'delete-page-confirmation-button',
        'data-test': 'delete-page-confirmation-button'
      },
      cancellationText: 'No, thanks.',
      cancellationButtonProps: {
        id: 'delete-page-cancellation-button',
        'data-test': 'delete-page-cancellation-button'
      },
      dialogProps: {
        PaperProps: {
          id: 'delete-page-confirmation-modal',
          'data-test': 'delete-page-confirmation-modal'
        }
      },
      titleProps: {
        id: 'delete-page-confirmation-title',
        'data-test': 'delete-page-confirmation-title'
      }
    }).then(() => {
      deletePage.trigger({
        skillBiteId: selectedSkillBite.id,
        sessionId: selectedSession.id,
        sectionId: selectedSection.id,
        pageId: selectedPage.id
      })
    }).catch(() => console.log('Delete page cancelled'))
  }

  if (!selectedSection) return null

  return (
    <Box className={classes.root}>
      <Box display='flex' flexDirection='row' alignItems='center'>
        <SectionIcon />
        <Typography className={classes.sectionName}>
          {selectedSection?.name}
        </Typography>
      </Box>

      <Box display='flex' flexDirection='row'>
        <FormControl sx={{ minWidth: 120, mr: 2 }} size='small'>
          <InputLabel id='skill-bite-page-selector-label'>Page</InputLabel>
          <Select
            labelId='skill-bite-page-selector-label'
            data-test='skill-bite-page-selector'
            value={selectedPage?.id ?? ''}
            onChange={(e) => handleChangePage({ id: e.target.value })}
            label='Page'
          >
            {selectedSection?.pages?.map((page, index) => (
              <MenuItem value={page.id} key={index}>Page {index + 1}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {canEditStructure && (
          <>
            <ActionButton
              variant='secondary'
              dataTest='skill-bite-add-page'
              disabled={addPage.isWorking}
              onClick={handleAddPage}
              sx={{ minWidth: 120 }}
              isWorking={addPage.isWorking}
            >
              Add Page
            </ActionButton>
          </>
        )}

        {canEditStructure && selectedPage && (
          <ActionButton
            variant='secondary'
            dataTest='skill-bite-delete-page'
            disabled={deletePage.isWorking}
            onClick={handleDeletePage}
            className={classes.deleteBtn}
            isWorking={deletePage.isWorking}
          >
            Delete Page
          </ActionButton>
        )}
      </Box>
    </Box>
  )
}

export default PageSelector
